/**
 * User-message metadata: strip + extract the hidden protocol blocks we prepend
 * to outgoing chat messages ([ROLE_CONTEXT], [SENDER], [TEACH]).
 *
 * WHY this exists: chat.history returns the raw message content that went over
 * the wire — including the hidden blocks. Before this utility, teammates
 * reopening a shared thread saw the raw tag JSON. Every render path that shows
 * user-authored content MUST pass it through extractUserMessageMeta first.
 *
 * Kept pure (no fetch/DOM) so it's trivially unit-testable.
 */

/** Identity block attached to outgoing messages. `stamp` is a server-issued
 *  countersign proving the auth server saw this user send into this thread —
 *  null when the countersign call failed (we NEVER block a send on it). */
export interface SenderMeta {
  username: string;
  name: string;
  stamp: string | null;
}

export interface UserMessageMeta {
  /** Message content with all protocol blocks removed. */
  cleanText: string;
  /** Parsed [SENDER] block, if present and well-formed. */
  sender?: SenderMeta;
}

/** Serialize a [SENDER] block for the compose path (see buildChatSend). */
export function buildSenderBlock(sender: SenderMeta): string {
  return `[SENDER]\n${JSON.stringify(sender)}\n[/SENDER]\n`;
}

// Matches a CLOSED [SENDER]...[/SENDER] block plus one trailing newline.
// We deliberately do NOT strip unclosed blocks (unlike parser.ts's FOREMAN_*
// sweep): user messages could legitimately contain a literal "[SENDER]" typed
// by a human, and eating everything after it would destroy their words. Real
// blocks are always machine-generated and closed.
const SENDER_BLOCK_RE = /\[SENDER\]\s*([\s\S]*?)\s*\[\/SENDER\]\n?/g;
const ROLE_CONTEXT_BLOCK_RE = /\[ROLE_CONTEXT\][\s\S]*?\[\/ROLE_CONTEXT\]\n*/g;

/**
 * Strip [ROLE_CONTEXT], [SENDER], and the [TEACH] prefix from raw user-message
 * content; return the clean text plus the parsed sender identity (if any).
 */
export function extractUserMessageMeta(raw: string): UserMessageMeta {
  let text = raw ?? '';
  let sender: SenderMeta | undefined;

  // Parse the FIRST [SENDER] block (there's only ever one per message), then
  // strip every occurrence so a malicious duplicate can't leak into the UI.
  SENDER_BLOCK_RE.lastIndex = 0;
  const m = SENDER_BLOCK_RE.exec(text);
  if (m) {
    try {
      const obj = JSON.parse(m[1]) as Record<string, unknown>;
      if (typeof obj.username === 'string' && obj.username.length > 0) {
        sender = {
          username: obj.username,
          name: typeof obj.name === 'string' && obj.name ? obj.name : obj.username,
          stamp: typeof obj.stamp === 'string' ? obj.stamp : null,
        };
      }
    } catch {
      // Malformed JSON — treat as "no sender recorded" rather than crash.
    }
  }
  text = text.replace(SENDER_BLOCK_RE, '');
  text = text.replace(ROLE_CONTEXT_BLOCK_RE, '');

  // Sweep any residual bare markers (orphan open/close tags) so a protocol
  // tag can never leak into the visible message — mirrors parser.ts.
  text = text.replace(/\[\/?(?:ROLE_CONTEXT|SENDER)\]/g, '');

  // [TEACH] is a plain prefix, not a block (see useMessages.sendMessage).
  text = text.replace(/^\s*\[TEACH\]\s*/, '');

  return { cleanText: text.trim(), sender };
}
